import type { DefaultOptions } from '@tanstack/react-query';
import { QueryClient } from '@tanstack/react-query';

/**
 * Default query options for React Query
 *
 * Configuration optimized for admin dashboard:
 * - 5 minute stale time: Data remains fresh for 5 minutes before refetching
 * - 10 minute cache time: Data stays in cache for 10 minutes after becoming unused
 * - Automatic request deduplication: Multiple calls to same query use single request
 * - Refetch on window focus: Ensures data freshness when user returns to tab
 * - Smart retry logic: Retries failed requests with exponential backoff
 */
const defaultQueryOptions: DefaultOptions = {
  queries: {
    // Data considered fresh for 5 minutes (prevents unnecessary refetches)
    staleTime: 5 * 60 * 1000, // 5 minutes

    // Keep unused data in cache for 10 minutes (allows quick navigation)
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly called 'cacheTime')

    // Refetch on window focus for critical data freshness
    // Disabled for specific queries where not needed
    refetchOnWindowFocus: true,

    // Refetch on mount only if data is stale
    refetchOnMount: true,

    // Don't refetch on network reconnect unless data is stale
    refetchOnReconnect: true,

    // Retry configuration with exponential backoff
    retry: (failureCount, error) => {
      // Don't retry on 4xx client errors (except 408 Request Timeout)
      if (error instanceof Error && 'status' in error) {
        const status = (error as { status: number }).status;
        if (status >= 400 && status < 500 && status !== 408) {
          return false;
        }
      }

      // Retry up to 3 times for network errors and 5xx errors
      return failureCount < 3;
    },

    // Exponential backoff delay: 1s, 2s, 4s
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  },

  mutations: {
    // Retry mutations once on network errors only
    retry: (failureCount, error) => {
      // Don't retry on HTTP errors (4xx, 5xx)
      if (error instanceof Error && 'status' in error) {
        return false;
      }

      // Retry once for network errors
      return failureCount < 1;
    },

    // Shorter retry delay for mutations (1 second)
    retryDelay: 1000,
  },
};

/**
 * Global QueryClient instance for the application
 *
 * Features:
 * - Automatic request deduplication (built-in)
 * - Intelligent background refetching
 * - Cache management and garbage collection
 * - Error handling and retry logic
 * - Optimistic updates support
 */
export const queryClient = new QueryClient({
  defaultOptions: defaultQueryOptions,
});

/**
 * Query client configuration for testing environments
 * Disables retries and uses shorter timeouts for faster test execution
 */
export const createTestQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        gcTime: 0,
        staleTime: 0,
      },
      mutations: {
        retry: false,
      },
    },
  });
